import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  RefreshControl,
  StatusBar,
  Alert,
  Modal,
  TextInput,
  Switch,
  ActivityIndicator,
  FlatList,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import { COMMERCE_DISABLED, CommerceUnavailable } from '../../../utils/iosCommerce';

const { width, height } = Dimensions.get('window');

// ==================== ✅ API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  // NEW: Multi-user-aware subscription endpoints (requires Bearer token via interceptor below)
  subscription:        `${API_BASE_URL}/api/business/subscription`,
  subscriptionPlans:   `${API_BASE_URL}/api/business/subscription/plans`,
  subscriptionRenew:   `${API_BASE_URL}/api/business/subscription/renew`,
  subscriptionAutoRenew: `${API_BASE_URL}/api/business/subscription/auto-renew`,
  subscriptionCancel:  `${API_BASE_URL}/api/business/subscription/cancel`,
  subscriptionResume:  `${API_BASE_URL}/api/business/subscription/resume`,
  subscriptionHistory: `${API_BASE_URL}/api/business/subscription/history`,
  // Unchanged — these still use legacy endpoints (no new replacement yet)
  paymentMethods:      `${API_BASE_URL}/api/payment-methods`,
  user:                `${API_BASE_URL}/api/data/users`,
};

// ✅ Axios instance with API key and auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ==================== ✅ SAFE HELPERS ====================
const safeString = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return String(value);
};

const safeReplace = (str, searchValue, replaceValue) => {
  const safeStr = safeString(str);
  if (!safeStr || typeof safeStr.replace !== 'function') return safeStr;
  return safeStr.replace(searchValue, replaceValue);
};

const safeToLowerCase = (str) => {
  const safeStr = safeString(str);
  if (!safeStr || typeof safeStr.toLowerCase !== 'function') return safeStr;
  return safeStr.toLowerCase();
};

const safeGet = (obj, path, defaultValue = null) => {
  try {
    if (!obj || typeof obj !== 'object') return defaultValue;
    const keys = path.split('.');
    let result = obj;
    for (const key of keys) {
      if (result === null || result === undefined || typeof result !== 'object') {
        return defaultValue;
      }
      result = result[key];
    }
    return result !== undefined ? result : defaultValue;
  } catch (error) {
    console.warn('Safe get error:', error);
    return defaultValue;
  }
};

// ==================== ✅ MODAL COMPONENTS ====================

// Plan Change Modal
const PlanChangeModal = ({ visible, currentPlan, plans, onClose, onChangePlan }) => {
  const [selectedPlan, setSelectedPlan] = useState(safeGet(currentPlan, 'id', ''));
  const [loading, setLoading] = useState(false);

  const handlePlanChange = async () => {
    const currentPlanId = safeGet(currentPlan, 'id', '');
    if (selectedPlan === currentPlanId) {
      Alert.alert('No Change', 'You have selected the same plan.');
      return;
    }

    setLoading(true);
    try {
      const safePlans = Array.isArray(plans) ? plans : [];
      const newPlan = safePlans.find(p => safeGet(p, 'id') === selectedPlan);
      if (newPlan && onChangePlan) {
        await onChangePlan(newPlan);
      }
      if (onClose) onClose();
    } catch (error) {
      console.error('Plan change error:', error);
      Alert.alert('Error', 'Failed to change plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const safePlans = Array.isArray(plans) ? plans : [];

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={onClose}>
            <Ionicons name="close" size={24} color="#64748B" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Change Plan</Text>
          <View style={{ width: 24 }} />
        </View>

        <ScrollView style={styles.modalContent}>
          <Text style={styles.modalSubtitle}>
            Choose a new plan for your subscription. Changes will take effect at the next billing cycle.
          </Text>

          {safePlans.map(plan => {
            const planId = safeGet(plan, 'id', '');
            const planName = safeGet(plan, 'name', 'Unknown Plan');
            const planPrice = safeGet(plan, 'price', 0);
            const planFeatures = safeGet(plan, 'features', []);
            const currentPlanId = safeGet(currentPlan, 'id', '');
            
            return (
              <TouchableOpacity
                key={planId}
                style={[
                  styles.planOption,
                  selectedPlan === planId && styles.selectedPlanOption,
                  currentPlanId === planId && styles.currentPlanOption
                ]}
                onPress={() => setSelectedPlan(planId)}
              >
                <View style={styles.planOptionHeader}>
                  <View style={styles.planOptionInfo}>
                    <Text style={styles.planOptionName}>{planName}</Text>
                    <Text style={styles.planOptionPrice}>£{planPrice}/month</Text>
                  </View>
                  <View style={styles.planOptionActions}>
                    {currentPlanId === planId && (
                      <View style={styles.currentPlanBadge}>
                        <Text style={styles.currentPlanBadgeText}>Current</Text>
                      </View>
                    )}
                    {selectedPlan === planId && (
                      <MaterialCommunityIcons name="check-circle" size={24} color="#10B981" />
                    )}
                  </View>
                </View>
                
                <View style={styles.planOptionFeatures}>
                  {Array.isArray(planFeatures) && planFeatures.slice(0, 3).map((feature, index) => (
                    <Text key={index} style={styles.planOptionFeature}>• {safeString(feature)}</Text>
                  ))}
                  {Array.isArray(planFeatures) && planFeatures.length > 3 && (
                    <Text style={styles.planOptionFeature}>• +{planFeatures.length - 3} more features</Text>
                  )}
                </View>
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        <View style={styles.modalActions}>
          <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.confirmButton, loading && styles.disabledButton]}
            onPress={handlePlanChange}
            disabled={loading}
          >
            {loading ? (
              <ActivityIndicator size={20} color="#fff" />
            ) : (
              <Text style={styles.confirmButtonText}>Change Plan</Text>
            )}
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );
};

// Billing History Modal
const BillingHistoryModal = ({ visible, onClose, billingHistory }) => {
  const formatDate = (dateString) => {
    try {
      const safeDate = safeString(dateString);
      if (!safeDate) return 'Unknown Date';
      const date = new Date(safeDate);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      console.warn('Date formatting error:', error);
      return 'Unknown Date';
    }
  };

  const getStatusColor = (status) => {
    const safeStatus = safeToLowerCase(status);
    switch (safeStatus) {
      case 'paid': return '#10B981';
      case 'pending': return '#F59E0B';
      case 'failed': return '#EF4444';
      default: return '#64748B';
    }
  };

  const safeBillingHistory = Array.isArray(billingHistory) ? billingHistory : [];

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={onClose}>
            <Ionicons name="close" size={24} color="#64748B" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Billing History</Text>
          <TouchableOpacity onPress={() => Alert.alert('Export', 'Export feature coming soon!')}>
            <MaterialCommunityIcons name="download" size={24} color="#4A90E2" />
          </TouchableOpacity>
        </View>

        <FlatList
          data={safeBillingHistory}
          keyExtractor={(item, index) => safeString(safeGet(item, 'id', index))}
          style={styles.modalContent}
          renderItem={({ item }) => {
            const description = safeString(safeGet(item, 'description', 'Unknown Transaction'));
            const amount = safeGet(item, 'amount', 0);
            const date = safeGet(item, 'date', '');
            const status = safeString(safeGet(item, 'status', 'unknown'));
            
            return (
              <View style={styles.billingItem}>
                <View style={styles.billingItemHeader}>
                  <Text style={styles.billingItemDescription}>{description}</Text>
                  <Text style={styles.billingItemAmount}>£{amount}</Text>
                </View>
                
                <View style={styles.billingItemDetails}>
                  <Text style={styles.billingItemDate}>{formatDate(date)}</Text>
                  <View style={[styles.billingItemStatus, { backgroundColor: getStatusColor(status) }]}>
                    <Text style={styles.billingItemStatusText}>{status}</Text>
                  </View>
                </View>
                
                <View style={styles.billingItemActions}>
                  <TouchableOpacity
                    style={styles.billingItemAction}
                    onPress={() => Alert.alert('Download Invoice', `Downloading invoice for ${description}`)}
                  >
                    <MaterialCommunityIcons name="download" size={16} color="#4A90E2" />
                    <Text style={styles.billingItemActionText}>Download</Text>
                  </TouchableOpacity>
                </View>
              </View>
            );
          }}
          showsVerticalScrollIndicator={false}
        />
      </SafeAreaView>
    </Modal>
  );
};

// Payment Methods Modal
const PaymentMethodsModal = ({ visible, onClose, paymentMethods, onAddMethod, onRemoveMethod, onSetDefault }) => {
  const safePaymentMethods = Array.isArray(paymentMethods) ? paymentMethods : [];

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={onClose}>
            <Ionicons name="close" size={24} color="#64748B" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Payment Methods</Text>
          <TouchableOpacity onPress={onAddMethod}>
            <MaterialCommunityIcons name="plus" size={24} color="#4A90E2" />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalContent}>
          {safePaymentMethods.map(method => {
            const methodId = safeGet(method, 'id', '');
            const methodType = safeString(safeGet(method, 'type', 'card'));
            const methodName = safeString(safeGet(method, 'name', 'Unknown Method'));
            const last4 = safeString(safeGet(method, 'last4', '0000'));
            const bank = safeString(safeGet(method, 'bank', 'Unknown Bank'));
            const isDefault = safeGet(method, 'isDefault', false);
            const expiryMonth = safeGet(method, 'expiryMonth', 1);
            const expiryYear = safeGet(method, 'expiryYear', 2025);
            
            return (
              <View key={methodId} style={styles.paymentMethodItem}>
                <View style={styles.paymentMethodIcon}>
                  <MaterialCommunityIcons
                    name={methodType === 'card' ? 'credit-card' : 'bank'}
                    size={24}
                    color="#4A90E2"
                  />
                </View>
                
                <View style={styles.paymentMethodDetails}>
                  <Text style={styles.paymentMethodName}>{methodName}</Text>
                  <Text style={styles.paymentMethodInfo}>
                    {methodType === 'card' 
                      ? `**** **** **** ${last4}` 
                      : `${bank} ****${last4}`}
                  </Text>
                  {methodType === 'card' && (
                    <Text style={styles.paymentMethodExpiry}>
                      Expires {expiryMonth.toString().padStart(2, '0')}/{expiryYear}
                    </Text>
                  )}
                </View>
                
                <View style={styles.paymentMethodActions}>
                  {isDefault && (
                    <View style={styles.defaultMethodBadge}>
                      <Text style={styles.defaultMethodBadgeText}>Default</Text>
                    </View>
                  )}
                  
                  <TouchableOpacity
                    style={styles.paymentMethodAction}
                    onPress={() => {
                      Alert.alert(
                        'Payment Method Options',
                        'What would you like to do?',
                        [
                          { text: 'Cancel', style: 'cancel' },
                          { text: 'Set as Default', onPress: () => onSetDefault && onSetDefault(methodId) },
                          { text: 'Remove', style: 'destructive', onPress: () => onRemoveMethod && onRemoveMethod(methodId) }
                        ]
                      );
                    }}
                  >
                    <MaterialCommunityIcons name="dots-vertical" size={20} color="#64748B" />
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}
          
          <TouchableOpacity style={styles.addPaymentMethodButton} onPress={onAddMethod}>
            <MaterialCommunityIcons name="plus-circle" size={24} color="#4A90E2" />
            <Text style={styles.addPaymentMethodText}>Add New Payment Method</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

// ==================== ✅ MAIN SCREEN ====================
export default function ManageSubscriptionScreen({ navigation }) {
  if (COMMERCE_DISABLED) return <CommerceUnavailable navigation={navigation} />;
  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [user, setUser] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  
  // Modal states
  const [showPlanChangeModal, setShowPlanChangeModal] = useState(false);
  const [showBillingHistoryModal, setShowBillingHistoryModal] = useState(false);
  const [showPaymentMethodsModal, setShowPaymentMethodsModal] = useState(false);
  
  // Settings states
  const [autoRenew, setAutoRenew] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [billingAlerts, setBillingAlerts] = useState(true);
  const [usageAlerts, setUsageAlerts] = useState(false);

  // Default plans fallback
  const defaultPlans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 99,
      features: [
        'Up to 5 job postings',
        'Basic applicant management',
        'Email support',
        'Standard templates',
        'Basic analytics'
      ]
    },
    {
      id: 'business',
      name: 'Business',
      price: 299,
      features: [
        'Unlimited job postings',
        'Advanced applicant management',
        'Team collaboration tools',
        'Custom branding',
        'Advanced analytics',
        'Priority support',
        'API access',
        'Custom integrations'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 599,
      features: [
        'Everything in Business',
        'Dedicated account manager',
        'Custom development',
        'SLA guarantee',
        'Advanced security',
        'Multi-location support',
        'White-label solution',
        'Custom reporting'
      ]
    }
  ];

  useEffect(() => {
    initializeManageSubscription();
  }, []);

  const initializeManageSubscription = async () => {
    try {
      setIsLoading(true);
      
      // Load local user/company data
      try {
        const userData = await AsyncStorage.getItem('user_data');
        if (userData) setUser(JSON.parse(userData));
      } catch (error) {
        console.warn('Error loading user data:', error);
      }
      
      try {
        const companyData = await AsyncStorage.getItem('company_profile');
        if (companyData) setCompanyProfile(JSON.parse(companyData));
      } catch (error) {
        console.warn('Error loading company data:', error);
      }

      // Fetch all API data
      await Promise.allSettled([
        fetchSubscription(),
        fetchAvailablePlans(),
        fetchBillingHistory(),
        fetchPaymentMethods(),
      ]);

    } catch (error) {
      console.error('Error initializing manage subscription:', error);
      Alert.alert('Error', 'Failed to load subscription data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const companyId = await AsyncStorage.getItem('company_id');
      // NEW endpoint returns { success, subscription: {...} } — single object with derived flags
      const response = await api.get(API_ENDPOINTS.subscription, {
        params: { company_id: companyId }
      });

      let subData = null;
      if (response.data?.success && response.data.subscription) {
        // New shape: { success, subscription: {...} }
        subData = response.data.subscription;
      } else if (response.data?.subscription) {
        subData = response.data.subscription;
      } else if (response.data?.success && response.data.data) {
        // Backward compat with legacy { success, data: [...] | {...} }
        subData = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
      } else if (response.data && typeof response.data === 'object' && response.data.plan_id) {
        subData = response.data;
      }

      if (subData) {
        setSubscription(subData);
        await AsyncStorage.setItem('business_subscription', JSON.stringify(subData));
        // Server is the source of truth. New shape uses auto_renew (snake_case).
        // Fall back to camelCase autoRenew for legacy responses.
        setAutoRenew(subData.auto_renew !== undefined ? !!subData.auto_renew : safeGet(subData, 'autoRenew', true));
      } else {
        const cached = await AsyncStorage.getItem('business_subscription');
        if (cached) {
          setSubscription(JSON.parse(cached));
        }
      }
    } catch (error) {
      console.warn('Error fetching subscription:', error.message);
      const cached = await AsyncStorage.getItem('business_subscription');
      if (cached) {
        setSubscription(JSON.parse(cached));
      }
    }
  };

  const fetchAvailablePlans = async () => {
    try {
      const response = await api.get(API_ENDPOINTS.subscriptionPlans);
      let plans = [];
      // New shape: { success, plans: [...] }; legacy: { success, data: [...] }
      if (response.data?.success && response.data.plans) {
        plans = response.data.plans;
      } else if (response.data?.success && response.data.data) {
        plans = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        plans = response.data;
      } else if (response.data && response.data.plans) {
        plans = response.data.plans;
      }
      setAvailablePlans(plans.length > 0 ? plans : defaultPlans);
    } catch (error) {
      console.warn('Error fetching plans:', error.message);
      setAvailablePlans(defaultPlans);
    }
  };

  const fetchBillingHistory = async () => {
    try {
      const companyId = await AsyncStorage.getItem('company_id');
      // NEW endpoint: /api/business/subscription/history?company_id=X
      // returns { success, history: [...] } — was previously per-subscription-id; now company-scoped
      const response = await api.get(API_ENDPOINTS.subscriptionHistory, {
        params: { company_id: companyId, limit: 50 },
      });

      let history = [];
      if (response.data?.success && response.data.history) {
        history = response.data.history;
      } else if (response.data?.success && response.data.data) {
        history = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        history = response.data;
      }
      setBillingHistory(history);
    } catch (error) {
      console.warn('Error fetching billing history:', error.message);
      setBillingHistory([]);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const companyId = await AsyncStorage.getItem('company_id');
      const response = await api.get(API_ENDPOINTS.paymentMethods, {
        params: { company_id: companyId }
      });
      
      let methods = [];
      if (response.data?.success && response.data.data) {
        methods = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        methods = response.data;
      }
      setPaymentMethods(methods);
    } catch (error) {
      console.warn('Error fetching payment methods:', error.message);
      setPaymentMethods([]);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await initializeManageSubscription();
    setRefreshing(false);
  };

  const navigateToScreen = (screenName, params = {}) => {
    try {
      if (navigation && navigation.navigate) {
        navigation.navigate(screenName, params);
      } else {
        Alert.alert('Navigation Error', `Cannot navigate to ${screenName}. Navigation not available.`);
      }
    } catch (error) {
      console.error('Navigation error:', error);
      Alert.alert('Navigation Error', `Failed to navigate to ${screenName}.`);
    }
  };

  const handlePlanChange = async (newPlan) => {
    try {
      if (!newPlan || !subscription) {
        throw new Error('Invalid plan or subscription data');
      }

      const planId = safeGet(newPlan, 'id', '');
      const planName = safeGet(newPlan, 'name', 'Unknown Plan');
      const planPrice = safeGet(newPlan, 'price', 0);
      const nextBillingDate = safeGet(subscription, 'nextBillingDate', new Date().toISOString());

      const updatedSubscription = {
        ...subscription,
        planId: planId,
        planName: planName,
        amount: planPrice,
        pendingChange: {
          planId: planId,
          planName: planName,
          amount: planPrice,
          effectiveDate: nextBillingDate
        }
      };
      
      setSubscription(updatedSubscription);
      await AsyncStorage.setItem('business_subscription', JSON.stringify(updatedSubscription));
      
      Alert.alert(
        'Plan Change Scheduled',
        `Your plan will be changed to ${planName} at the next billing cycle on ${formatDate(nextBillingDate)}.`
      );
      
    } catch (error) {
      console.error('Error changing plan:', error);
      throw error;
    }
  };

  const handleAutoRenewToggle = async (value) => {
    // Optimistic UI flip so the toggle feels responsive; revert on failure
    const previousValue = autoRenew;
    setAutoRenew(value);

    try {
      const companyId = await AsyncStorage.getItem('company_id');
      const response = await api.put(
        API_ENDPOINTS.subscriptionAutoRenew,
        { enabled: value },
        { params: { company_id: companyId } }
      );

      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Server rejected the change');
      }

      // Persist new state locally — server confirmed
      if (subscription) {
        const updatedSubscription = { ...subscription, auto_renew: value, autoRenew: value };
        setSubscription(updatedSubscription);
        await AsyncStorage.setItem('business_subscription', JSON.stringify(updatedSubscription));
      }

      Alert.alert(
        'Auto-Renewal Updated',
        value
          ? 'Your subscription will automatically renew at the end of each billing period.'
          : 'Your subscription will not automatically renew. You will need to manually renew before it expires.'
      );
    } catch (error) {
      console.error('Error updating auto-renewal:', error?.response?.data || error.message);
      // Revert the toggle UI
      setAutoRenew(previousValue);
      Alert.alert('Error', error?.response?.data?.error || 'Failed to update auto-renewal setting. Please try again.');
    }
  };

  const handleCancelSubscription = () => {
    Alert.alert(
      'Cancel Subscription',
      'Are you sure you want to cancel your subscription? You will continue to have access until the end of your current billing period.',
      [
        { text: 'Keep Subscription', style: 'cancel' },
        {
          text: 'Cancel Subscription',
          style: 'destructive',
          onPress: async () => {
            try {
              if (!subscription) {
                Alert.alert('Error', 'No subscription found to cancel.');
                return;
              }

              const companyId = await AsyncStorage.getItem('company_id');
              const response = await api.post(
                API_ENDPOINTS.subscriptionCancel,
                { reason: 'Cancelled from ManageSubscription screen' },
                { params: { company_id: companyId } }
              );

              if (!response.data?.success) {
                throw new Error(response.data?.error || 'Server rejected the cancellation');
              }

              // Server confirmed — update local state from the server response
              const cancelledAt = response.data.cancelled_at || new Date().toISOString();
              const accessUntil = response.data.access_until || safeGet(subscription, 'end_date', null);

              const updatedSubscription = {
                ...subscription,
                cancelled_at: cancelledAt,
                cancelledAt,           // legacy camelCase mirror
                accessUntil,
                auto_renew: false,
                autoRenew: false,
              };
              setSubscription(updatedSubscription);
              setAutoRenew(false);
              await AsyncStorage.setItem('business_subscription', JSON.stringify(updatedSubscription));

              Alert.alert(
                'Subscription Cancelled',
                response.data.message || 'Your subscription has been cancelled. You will continue to have access until the end of your current billing period.',
                [{ text: 'OK', onPress: () => navigation && navigation.goBack && navigation.goBack() }]
              );

            } catch (error) {
              console.error('Error cancelling subscription:', error?.response?.data || error.message);
              Alert.alert('Error', error?.response?.data?.error || 'Failed to cancel subscription. Please try again.');
            }
          }
        }
      ]
    );
  };

  const handleAddPaymentMethod = () => {
    Alert.alert('Add Payment Method', 'Add payment method feature coming soon!');
  };

  const handleRemovePaymentMethod = (methodId) => {
    Alert.alert(
      'Remove Payment Method',
      'Are you sure you want to remove this payment method?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            try {
              const updatedMethods = paymentMethods.filter(method => safeGet(method, 'id') !== methodId);
              setPaymentMethods(updatedMethods);
              Alert.alert('Success', 'Payment method removed successfully.');
            } catch (error) {
              console.error('Error removing payment method:', error);
              Alert.alert('Error', 'Failed to remove payment method.');
            }
          }
        }
      ]
    );
  };

  const handleSetDefaultPaymentMethod = (methodId) => {
    try {
      const updatedMethods = paymentMethods.map(method => ({
        ...method,
        isDefault: safeGet(method, 'id') === methodId
      }));
      setPaymentMethods(updatedMethods);
      Alert.alert('Success', 'Default payment method updated.');
    } catch (error) {
      console.error('Error setting default payment method:', error);
      Alert.alert('Error', 'Failed to update default payment method.');
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    try {
      const safeAmount = typeof amount === 'number' ? amount : 0;
      const symbol = currency === 'GBP' ? '£' : '$';
      return `${symbol}${safeAmount}`;
    } catch (error) {
      console.warn('Currency formatting error:', error);
      return '£0';
    }
  };

  const formatDate = (dateString) => {
    try {
      const safeDate = safeString(dateString);
      if (!safeDate) return 'Unknown Date';
      const date = new Date(safeDate);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      console.warn('Date formatting error:', error);
      return 'Unknown Date';
    }
  };

  const getCurrentPlan = () => {
    try {
      const subscriptionPlanId = safeGet(subscription, 'planId', safeGet(subscription, 'plan_id', 'business'));
      const foundPlan = availablePlans.find(plan => safeGet(plan, 'id') === subscriptionPlanId);
      return foundPlan || (availablePlans.length > 0 ? availablePlans[1] : defaultPlans[1]);
    } catch (error) {
      console.warn('Error getting current plan:', error);
      return defaultPlans[1];
    }
  };

  const getStatusColor = (status) => {
    const safeStatus = safeToLowerCase(status);
    switch (safeStatus) {
      case 'active': return '#10B981';
      case 'trial': return '#F59E0B';
      case 'cancelled': return '#EF4444';
      case 'expired': return '#94A3B8';
      default: return '#64748B';
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#4A90E2" />
        <Text style={styles.loadingText}>Loading subscription details...</Text>
      </SafeAreaView>
    );
  }

  const currentPlan = getCurrentPlan();
  const subscriptionStatus = safeString(safeGet(subscription, 'status', 'active'));
  const nextBillingDate = safeGet(subscription, 'nextBillingDate', '');
  const pendingChange = safeGet(subscription, 'pendingChange', null);

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#4A90E2" />
      
      <LinearGradient colors={['#4A90E2', '#357ABD']} style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation && navigation.goBack ? navigation.goBack() : null}
        >
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Manage Subscription</Text>
        <TouchableOpacity
          style={styles.helpButton}
          onPress={() => Alert.alert('Help', 'Need help managing your subscription? Contact our support team!')}
        >
          <Ionicons name="help-circle" size={24} color="#fff" />
        </TouchableOpacity>
      </LinearGradient>

      <ScrollView
        style={styles.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.subscriptionCard}>
          <View style={styles.subscriptionHeader}>
            <Text style={styles.subscriptionTitle}>Current Subscription</Text>
            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(subscriptionStatus) }]}>
              <Text style={styles.statusBadgeText}>{subscriptionStatus}</Text>
            </View>
          </View>
          
          <View style={styles.subscriptionDetails}>
            <Text style={styles.planName}>{safeGet(currentPlan, 'name', 'Unknown Plan')}</Text>
            <Text style={styles.planPrice}>{formatCurrency(safeGet(currentPlan, 'price', 0))}/month</Text>
            
            {nextBillingDate && (
              <Text style={styles.nextBilling}>
                Next billing: {formatDate(nextBillingDate)}
              </Text>
            )}
            
            {pendingChange && (
              <View style={styles.pendingChange}>
                <MaterialCommunityIcons name="clock-outline" size={16} color="#F59E0B" />
                <Text style={styles.pendingChangeText}>
                  Plan change to {safeGet(pendingChange, 'planName', 'Unknown Plan')} scheduled for {formatDate(safeGet(pendingChange, 'effectiveDate', ''))}
                </Text>
              </View>
            )}
          </View>

          <View style={styles.subscriptionActions}>
            <TouchableOpacity
              style={styles.changePlanButton}
              onPress={() => setShowPlanChangeModal(true)}
            >
              <MaterialCommunityIcons name="swap-horizontal" size={16} color="#4A90E2" />
              <Text style={styles.changePlanButtonText}>Change Plan</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.viewPlansButton}
              onPress={() => navigateToScreen('BusinessSubscriptionSystem')}
            >
              <MaterialCommunityIcons name="eye" size={16} color="#10B981" />
              <Text style={styles.viewPlansButtonText}>View All Plans</Text>
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Billing & Payment</Text>
          
          <TouchableOpacity
            style={styles.sectionItem}
            onPress={() => setShowBillingHistoryModal(true)}
          >
            <View style={styles.sectionItemIcon}>
              <MaterialCommunityIcons name="file-document" size={24} color="#4A90E2" />
            </View>
            <View style={styles.sectionItemContent}>
              <Text style={styles.sectionItemTitle}>Billing History</Text>
              <Text style={styles.sectionItemSubtitle}>View past invoices and payments</Text>
            </View>
            <MaterialCommunityIcons name="chevron-right" size={20} color="#94A3B8" />
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.sectionItem}
            onPress={() => setShowPaymentMethodsModal(true)}
          >
            <View style={styles.sectionItemIcon}>
              <MaterialCommunityIcons name="credit-card" size={24} color="#10B981" />
            </View>
            <View style={styles.sectionItemContent}>
              <Text style={styles.sectionItemTitle}>Payment Methods</Text>
              <Text style={styles.sectionItemSubtitle}>Manage your payment methods</Text>
            </View>
            <MaterialCommunityIcons name="chevron-right" size={20} color="#94A3B8" />
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.sectionItem}
            onPress={() => navigateToScreen('BusinessPaymentSystem')}
          >
            <View style={styles.sectionItemIcon}>
              <MaterialCommunityIcons name="receipt" size={24} color="#F59E0B" />
            </View>
            <View style={styles.sectionItemContent}>
              <Text style={styles.sectionItemTitle}>Payment System</Text>
              <Text style={styles.sectionItemSubtitle}>Access full payment management</Text>
            </View>
            <MaterialCommunityIcons name="chevron-right" size={20} color="#94A3B8" />
          </TouchableOpacity>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Subscription Settings</Text>
          
          <View style={styles.settingItem}>
            <View style={styles.settingItemContent}>
              <Text style={styles.settingItemTitle}>Auto-Renewal</Text>
              <Text style={styles.settingItemSubtitle}>
                Automatically renew your subscription each billing period
              </Text>
            </View>
            <Switch
              value={autoRenew}
              onValueChange={handleAutoRenewToggle}
              trackColor={{ false: '#E2E8F0', true: '#4A90E2' }}
              thumbColor={autoRenew ? '#fff' : '#fff'}
            />
          </View>
          
          <View style={styles.settingItem}>
            <View style={styles.settingItemContent}>
              <Text style={styles.settingItemTitle}>Email Notifications</Text>
              <Text style={styles.settingItemSubtitle}>
                Receive email updates about your subscription
              </Text>
            </View>
            <Switch
              value={emailNotifications}
              onValueChange={setEmailNotifications}
              trackColor={{ false: '#E2E8F0', true: '#4A90E2' }}
              thumbColor={emailNotifications ? '#fff' : '#fff'}
            />
          </View>
          
          <View style={styles.settingItem}>
            <View style={styles.settingItemContent}>
              <Text style={styles.settingItemTitle}>Billing Alerts</Text>
              <Text style={styles.settingItemSubtitle}>
                Get notified before your next billing date
              </Text>
            </View>
            <Switch
              value={billingAlerts}
              onValueChange={setBillingAlerts}
              trackColor={{ false: '#E2E8F0', true: '#4A90E2' }}
              thumbColor={billingAlerts ? '#fff' : '#fff'}
            />
          </View>
          
          <View style={styles.settingItem}>
            <View style={styles.settingItemContent}>
              <Text style={styles.settingItemTitle}>Usage Alerts</Text>
              <Text style={styles.settingItemSubtitle}>
                Get notified when approaching plan limits
              </Text>
            </View>
            <Switch
              value={usageAlerts}
              onValueChange={setUsageAlerts}
              trackColor={{ false: '#E2E8F0', true: '#4A90E2' }}
              thumbColor={usageAlerts ? '#fff' : '#fff'}
            />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Usage & Analytics</Text>
          
          <View style={styles.usageCard}>
            <View style={styles.usageItem}>
              <Text style={styles.usageLabel}>Job Postings</Text>
              <Text style={styles.usageValue}>12 / Unlimited</Text>
              <View style={styles.usageBar}>
                <View style={[styles.usageProgress, { width: '20%' }]} />
              </View>
            </View>
            
            <View style={styles.usageItem}>
              <Text style={styles.usageLabel}>Team Members</Text>
              <Text style={styles.usageValue}>5 / Unlimited</Text>
              <View style={styles.usageBar}>
                <View style={[styles.usageProgress, { width: '15%' }]} />
              </View>
            </View>
            
            <View style={styles.usageItem}>
              <Text style={styles.usageLabel}>Storage Used</Text>
              <Text style={styles.usageValue}>2.3 GB / 100 GB</Text>
              <View style={styles.usageBar}>
                <View style={[styles.usageProgress, { width: '2.3%' }]} />
              </View>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Support & Help</Text>
          
          <TouchableOpacity
            style={styles.sectionItem}
            onPress={() => Alert.alert('Contact Support', 'Support contact feature coming soon!')}
          >
            <View style={styles.sectionItemIcon}>
              <MaterialCommunityIcons name="headset" size={24} color="#8B5CF6" />
            </View>
            <View style={styles.sectionItemContent}>
              <Text style={styles.sectionItemTitle}>Contact Support</Text>
              <Text style={styles.sectionItemSubtitle}>Get help with your subscription</Text>
            </View>
            <MaterialCommunityIcons name="chevron-right" size={20} color="#94A3B8" />
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.sectionItem}
            onPress={() => Alert.alert('FAQ', 'FAQ feature coming soon!')}
          >
            <View style={styles.sectionItemIcon}>
              <MaterialCommunityIcons name="help-circle" size={24} color="#06B6D4" />
            </View>
            <View style={styles.sectionItemContent}>
              <Text style={styles.sectionItemTitle}>FAQ</Text>
              <Text style={styles.sectionItemSubtitle}>Frequently asked questions</Text>
            </View>
            <MaterialCommunityIcons name="chevron-right" size={20} color="#94A3B8" />
          </TouchableOpacity>
        </View>

        <View style={styles.dangerSection}>
          <Text style={styles.dangerSectionTitle}>Danger Zone</Text>
          <TouchableOpacity
            style={styles.dangerButton}
            onPress={handleCancelSubscription}
          >
            <MaterialCommunityIcons name="cancel" size={20} color="#EF4444" />
            <Text style={styles.dangerButtonText}>Cancel Subscription</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      <PlanChangeModal
        visible={showPlanChangeModal}
        currentPlan={currentPlan}
        plans={availablePlans}
        onClose={() => setShowPlanChangeModal(false)}
        onChangePlan={handlePlanChange}
      />

      <BillingHistoryModal
        visible={showBillingHistoryModal}
        onClose={() => setShowBillingHistoryModal(false)}
        billingHistory={billingHistory}
      />

      <PaymentMethodsModal
        visible={showPaymentMethodsModal}
        onClose={() => setShowPaymentMethodsModal(false)}
        paymentMethods={paymentMethods}
        onAddMethod={handleAddPaymentMethod}
        onRemoveMethod={handleRemovePaymentMethod}
        onSetDefault={handleSetDefaultPaymentMethod}
      />
    </SafeAreaView>
  );
}

// ==================== ✅ STYLES ====================
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: 20,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
  },
  helpButton: {
    padding: 8,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  subscriptionCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  subscriptionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  subscriptionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1E293B',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusBadgeText: {
    fontSize: 10,
    color: '#fff',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  subscriptionDetails: {
    marginBottom: 20,
  },
  planName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1E293B',
    marginBottom: 4,
  },
  planPrice: {
    fontSize: 18,
    color: '#4A90E2',
    fontWeight: '600',
    marginBottom: 8,
  },
  nextBilling: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  pendingChange: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFBEB',
    padding: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FED7AA',
  },
  pendingChangeText: {
    fontSize: 12,
    color: '#92400E',
    marginLeft: 4,
    flex: 1,
  },
  subscriptionActions: {
    flexDirection: 'row',
    gap: 12,
  },
  changePlanButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EBF4FF',
    paddingVertical: 12,
    borderRadius: 8,
  },
  changePlanButtonText: {
    color: '#4A90E2',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 4,
  },
  viewPlansButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F0FDF4',
    paddingVertical: 12,
    borderRadius: 8,
  },
  viewPlansButtonText: {
    color: '#10B981',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 4,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 12,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1E293B',
    padding: 20,
    paddingBottom: 0,
  },
  sectionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  sectionItemIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F8FAFC',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  sectionItemContent: {
    flex: 1,
  },
  sectionItemTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 2,
  },
  sectionItemSubtitle: {
    fontSize: 12,
    color: '#64748B',
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  settingItemContent: {
    flex: 1,
  },
  settingItemTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 2,
  },
  settingItemSubtitle: {
    fontSize: 12,
    color: '#64748B',
  },
  usageCard: {
    padding: 20,
  },
  usageItem: {
    marginBottom: 16,
  },
  usageLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  usageValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 8,
  },
  usageBar: {
    height: 4,
    backgroundColor: '#E2E8F0',
    borderRadius: 2,
  },
  usageProgress: {
    height: '100%',
    backgroundColor: '#4A90E2',
    borderRadius: 2,
  },
  dangerSection: {
    backgroundColor: '#FEF2F2',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#FECACA',
  },
  dangerSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#DC2626',
    marginBottom: 12,
  },
  dangerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#EF4444',
  },
  dangerButtonText: {
    color: '#EF4444',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#fff',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1E293B',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  modalSubtitle: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 20,
    lineHeight: 20,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#F8FAFC',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: '#64748B',
    fontSize: 16,
    fontWeight: '600',
  },
  confirmButton: {
    flex: 1,
    backgroundColor: '#4A90E2',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  confirmButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  disabledButton: {
    backgroundColor: '#E2E8F0',
  },
  planOption: {
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: '#E2E8F0',
  },
  selectedPlanOption: {
    borderColor: '#10B981',
    backgroundColor: '#F0FDF4',
  },
  currentPlanOption: {
    borderColor: '#4A90E2',
    backgroundColor: '#EBF4FF',
  },
  planOptionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  planOptionInfo: {
    flex: 1,
  },
  planOptionName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1E293B',
    marginBottom: 2,
  },
  planOptionPrice: {
    fontSize: 14,
    color: '#4A90E2',
    fontWeight: '600',
  },
  planOptionActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  currentPlanBadge: {
    backgroundColor: '#4A90E2',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  currentPlanBadgeText: {
    fontSize: 10,
    color: '#fff',
    fontWeight: '600',
  },
  planOptionFeatures: {
    marginTop: 8,
  },
  planOptionFeature: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 2,
  },
  billingItem: {
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  billingItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  billingItemDescription: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1E293B',
    flex: 1,
  },
  billingItemAmount: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#10B981',
  },
  billingItemDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  billingItemDate: {
    fontSize: 12,
    color: '#64748B',
  },
  billingItemStatus: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  billingItemStatusText: {
    fontSize: 10,
    color: '#fff',
    fontWeight: '500',
    textTransform: 'uppercase',
  },
  billingItemActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  billingItemAction: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#EBF4FF',
  },
  billingItemActionText: {
    fontSize: 12,
    color: '#4A90E2',
    fontWeight: '500',
    marginLeft: 4,
  },
  paymentMethodItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  paymentMethodIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#EBF4FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  paymentMethodDetails: {
    flex: 1,
  },
  paymentMethodName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 2,
  },
  paymentMethodInfo: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 2,
  },
  paymentMethodExpiry: {
    fontSize: 10,
    color: '#94A3B8',
  },
  paymentMethodActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  defaultMethodBadge: {
    backgroundColor: '#10B981',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  defaultMethodBadgeText: {
    fontSize: 10,
    color: '#fff',
    fontWeight: '600',
  },
  paymentMethodAction: {
    padding: 8,
  },
  addPaymentMethodButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EBF4FF',
    paddingVertical: 16,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#4A90E2',
    borderStyle: 'dashed',
  },
  addPaymentMethodText: {
    color: '#4A90E2',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
});